import { createSelector } from 'reselect';
// Import state types.
import {
	AppState,
	BroadLineOfBusinessState,
} from '@app/redux/states';
// Import props.
import {
	BroadLineOfBusinessProps,
} from '@app/props';
// Import api models.
import {
	PossibleBuildingUse,
	PossibleConstructionType,
	PossibleFoundationType,
	PossibleOccupancyType,
	PossibleParkingType,
} from '@app/models/api';
// Import enumerations.
import {
	BroadLineOfBusiness
} from '@app/enumerations';

// Fetches the broad lines of business state.
const getBroadLineOfBusinessState = (state: AppState, props: BroadLineOfBusinessProps): BroadLineOfBusinessState | undefined => {
	return state.possibleBroadLinesOfBusiness[props.broadLineOfBusinessCode];
}

// Fetches the broad lines of business commercial state.
const getBroadLineOfBusinessCommercialState = (state: AppState): BroadLineOfBusinessState | undefined => {
	return state.possibleBroadLinesOfBusiness[BroadLineOfBusiness.COMMERCIAL];
}

// Fetches the broad lines of business personal state.
const getBroadLineOfBusinessPersonalState = (state: AppState): BroadLineOfBusinessState | undefined => {
	return state.possibleBroadLinesOfBusiness[BroadLineOfBusiness.PERSONAL];
}

// Get Possible Building Uses Selector.
const makeGetPossibleBuildingUsesSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): PossibleBuildingUse[] => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.possibleBuildingUses
			: [];
	}
);

// Get Possible Construction Types Selector.
const makeGetPossibleConstructionTypesSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): PossibleConstructionType[] => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.possibleConstructionTypes
			: [];
	}
);

// Get Possible Construction Types Commercial Selector.
const makeGetPossibleConstructionTypesCommercialSelector = () => createSelector(
	[getBroadLineOfBusinessCommercialState],
	(broadLineOfBusinessCommercialState: BroadLineOfBusinessState | undefined): PossibleConstructionType[] => {
		return broadLineOfBusinessCommercialState?.possibleConstructionTypes ?? [];
	}
);

// Get Possible Construction Types Personal Selector.
const makeGetPossibleConstructionTypesPersonalSelector = () => createSelector(
	[getBroadLineOfBusinessPersonalState],
	(broadLineOfBusinessPersonalState: BroadLineOfBusinessState | undefined): PossibleConstructionType[] => {
		return broadLineOfBusinessPersonalState?.possibleConstructionTypes ?? [];
	}
);

// Get Possible Foundation Types Selector.
const makeGetPossibleFoundationTypesSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): PossibleFoundationType[] => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.possibleFoundationTypes
			: [];
	}
);

// Get Possible Occupancy Types Selector.
const makeGetPossibleOccupancyTypesSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): PossibleOccupancyType[] => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.possibleOccupancyTypes
			: [];
	}
);

// Get Possible Parking Types Selector.
const makeGetPossibleParkingTypesSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): PossibleParkingType[] => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.possibleParkingTypes
			: [];
	}
);

// Are Possible Building Uses Loaded Selector.
const makeArePossibleBuildingUsesLoadedSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): boolean => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.arePossibleBuildingUsesLoaded
			: false;
	}
);

// Are Possible Construction Types Loaded Selector.
const makeArePossibleConstructionTypesLoadedSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): boolean => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.arePossibleConstructionTypesLoaded
			: false;
	}
);

// Are Possible Foundation Types Loaded Selector.
const makeArePossibleFoundationTypesLoadedSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): boolean => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.arePossibleFoundationTypesLoaded
			: false;
	}
);

// Are Possible Occupancy Types Loaded Selector.
const makeArePossibleOccupancyTypesLoadedSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): boolean => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.arePossibleOccupancyTypesLoaded
			: false;
	}
);

// Are Possible Parking Types Loaded Selector.
const makeArePossibleParkingTypesLoadedSelector = () => createSelector(
	[getBroadLineOfBusinessState],
	(broadLineOfBusinessState: BroadLineOfBusinessState | undefined): boolean => {
		return broadLineOfBusinessState !== undefined
			? broadLineOfBusinessState.arePossibleParkingTypesLoaded
			: false;
	}
);

// Export composed methods.
export {
	getBroadLineOfBusinessState,
	getBroadLineOfBusinessCommercialState,
	getBroadLineOfBusinessPersonalState,
};

// export selectors.
export {
	makeGetPossibleBuildingUsesSelector,
	makeGetPossibleConstructionTypesSelector,
	makeGetPossibleConstructionTypesCommercialSelector,
	makeGetPossibleConstructionTypesPersonalSelector,
	makeGetPossibleFoundationTypesSelector,
	makeGetPossibleOccupancyTypesSelector,
	makeGetPossibleParkingTypesSelector,
	makeArePossibleBuildingUsesLoadedSelector,
	makeArePossibleConstructionTypesLoadedSelector,
	makeArePossibleFoundationTypesLoadedSelector,
	makeArePossibleOccupancyTypesLoadedSelector,
	makeArePossibleParkingTypesLoadedSelector,
};